import * as React from "react";
import { useState, useEffect, useRef, useImperativeHandle } from "react";
import styled from "styled-components";
import { CarouselSettingsAttributes } from "../attributes/CarouselSettings";

interface CarouselBaseProps {
  settings: CarouselSettingsAttributes;
  children: React.ReactNode | React.ReactNode[];
  onUpdateHasRight: (hasRight: boolean) => void;
  onUpdateHasLeft: (hasLeft: boolean) => void;
}

export interface CarouselBaseMethods {
  handleMove: (direction: "left" | "right") => void;
}

const CarouselBase = React.forwardRef<CarouselBaseMethods, CarouselBaseProps>(
  (
    { settings, children, onUpdateHasRight, onUpdateHasLeft }: CarouselBaseProps,
    ref
  ): JSX.Element => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [offsetsArray, setOffsetsArray] = useState<number[]>([]);
    const prevScrollLeftRef = useRef(0);
    const isScrollEventListenerRef = useRef(false);
    const innerRef = useRef<HTMLDivElement>(null);
    const isMannualScrolling = useRef<boolean>();
    const mutationObserver = useRef<MutationObserver | null>(null);
    const resizeObserver = useRef<ResizeObserver | null>(null);
    //   settings prop extraction
    const { autoPlay, autoPlayInterval, sliderSnap } = settings || {};

    //   methods to be exposed to parent
    useImperativeHandle(ref, () => ({
      handleMove,
    }));

    // check and update if has more to left or right (api for parent)
    useEffect(() => {
      const isAtFirstSlide = currentSlide <= 0;
      const isAtLastSlide = currentSlide >= offsetsArray.length - 1;
      onUpdateHasLeft(!isAtFirstSlide);
      onUpdateHasRight(!isAtLastSlide);
    }, [currentSlide, offsetsArray]);

    // handle scroll use effect
    useEffect(() => {
      const element = innerRef.current;
      const handleWheelEvent = () => {
        isMannualScrolling.current = true;
      };
      if (sliderSnap && element && !isScrollEventListenerRef.current) {
        isScrollEventListenerRef.current = true;
        element.addEventListener("scrollend", handleScrollEnd);
        element.addEventListener("wheel", handleWheelEvent);
        element.addEventListener("touchstart", handleWheelEvent);
        return () => {
          element.removeEventListener("scrollend", handleScrollEnd);
          element.removeEventListener("wheel", handleWheelEvent);
          element.removeEventListener("touchstart", handleWheelEvent);
          isScrollEventListenerRef.current = false;
        }; // else if handels when slider is toggeled off
      } else if (element && !sliderSnap && isScrollEventListenerRef.current) {
        element.removeEventListener("scrollend", handleScrollEnd);
        element.removeEventListener("wheel", handleWheelEvent);
        element.removeEventListener("touchstart", handleWheelEvent);
        isScrollEventListenerRef.current = false;
      }
    }, [offsetsArray, innerRef, sliderSnap]);

    //   auto play use effect
    useEffect(() => {
      let interval: NodeJS.Timeout;
      // console.log(
      //   "in autoplay effect - autoPlay, autoPlayInterval, offsetsArray, currentSlide",
      //   autoPlay,
      //   autoPlayInterval,
      //   offsetsArray,
      //   currentSlide
      // );

      if (currentSlide === offsetsArray.length) return;
      if (autoPlay && autoPlayInterval) {
        interval = setInterval(() => {
          handleMove("right");
        }, autoPlayInterval);
      }

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }, [currentSlide, autoPlay, autoPlayInterval, offsetsArray]);

    //   inner reference use effect
    useEffect(() => {
      // console.log(
      //   "in innerRef effect - autoPlayInterval, offsetsArray, currentSlide",
      //   autoPlayInterval,
      //   offsetsArray,
      //   currentSlide
      // );
      const element = innerRef.current;
      if (element) {
        // console.log("in innerRef effect - element, children", element, children);
        const updateOffsets = () => {
          const array = createOffsetArray(element); // ** Updated
          setOffsetsArray(array); // ** Updated
        };
        updateOffsets();
        // due to possible changes in items (in editor) and fetching items, update items offsets array by observer
        const observer = new MutationObserver((mutations) => {
          updateOffsets();
        });
        observer.observe(element, { childList: true });
        mutationObserver.current = observer;

        const resizeObs = new ResizeObserver(() => {
          // console.log("exec resizeObs");

          updateOffsets(); // ** Updated without delay
        });
        resizeObs.observe(element);
        resizeObserver.current = resizeObs;

        return () => {
          if (mutationObserver.current) {
            mutationObserver.current.disconnect();
          }
          if (resizeObserver.current) {
            resizeObserver.current.disconnect(); // ** Disconnect ResizeObserver
          }
        };
      }
    }, [innerRef, children]);
    // console.log(
    //   "in render - autoPlayInterval, offsetsArray, currentSlide",
    //   autoPlayInterval,
    //   offsetsArray,
    //   currentSlide
    // );
    //   next/prev function
    const handleMove = (direction: "left" | "right") => {
      const innerContainer = innerRef.current;
      if (!innerContainer) return;
      const lastIndex = offsetsArray.length - 1;
      const currentOffset = offsetsArray[currentSlide];

      // handel left button
      if (direction === "left" && currentSlide > 0) {
        let previousItemIndex = currentSlide - 1;

        // if there are more then two items in view at end of scroll, prev skips offsets of items inside view
        if (currentSlide === lastIndex)
          while (
            previousItemIndex >= 0 &&
            offsetsArray[previousItemIndex] >= currentOffset - innerContainer.clientWidth
          ) {
            previousItemIndex--;
          }

        scrollInnerTo(innerContainer, Math.max(previousItemIndex, 0));

        // handle right button
      } else if (direction === "right" && currentSlide < lastIndex) {
        // check if all last items are in view
        const lastItemsInView = offsetsArray
          .slice(currentSlide, lastIndex + 1)
          .every((offset) => offset <= currentOffset + innerContainer.clientWidth);

        if (lastItemsInView) {
          // Scroll to the last item if all last items are in view
          scrollInnerTo(innerContainer, lastIndex);
        } else {
          // Otherwise, scroll to the next item
          const nextItemIndex = currentSlide + 1;
          scrollInnerTo(innerContainer, nextItemIndex);
        }
      }
    };

    //   scrollEnd event function
    function handleScrollEnd(this: HTMLDivElement, e: Event) {
      if (!isMannualScrolling.current) {
        // Ignore if it's a second scroll event triggered by scrollTo
        return;
      }
      // Update previous scroll position
      const scrollLeft = this.scrollLeft;
      prevScrollLeftRef.current = scrollLeft;

      // Find the nearest offset to the current scroll position
      const distances = offsetsArray.map((val) => Math.abs(scrollLeft - val));
      const nearestOffset = distances.indexOf(Math.min(...distances));

      // Determine if should go to nearest item or stick to end
      const contentWidth = this.scrollWidth;
      const containerWidth = this.clientWidth;
      const distanceToContainerEnd = contentWidth - containerWidth - scrollLeft;
      const targetIndex =
        distanceToContainerEnd < distances[nearestOffset] ? offsetsArray.length - 1 : nearestOffset;
      // console.log("distanceToContainerEnd", distanceToContainerEnd);
      // console.log("distances", distances);
      // console.log("nearestOffset", nearestOffset);
      // console.log("distances[nearestOffset]", distances[nearestOffset]);
      // console.log("targetIndex", targetIndex);

      scrollInnerTo(this, targetIndex);
    }

    //   inner scroll function
    const scrollInnerTo = (innerReference: HTMLDivElement, index: number) => {
      if (innerReference) {
        const item = offsetsArray[index];
        innerReference.scrollTo({
          left: item,
          behavior: "smooth",
        });

        // ignore scroll left  next update
        isMannualScrolling.current = false;

        setCurrentSlide(index);
      }
    };

    return (
      <BaseCarouselTheme>
        <div className="carousel-inner" ref={innerRef}>
          {children}
        </div>
      </BaseCarouselTheme>
    );
  }
);

export default CarouselBase;

const createOffsetArray = (el: HTMLDivElement) => {
  return Array.from(el.children)
    .filter((child) => child.classList.contains("carousel-item"))
    .map((item) => {
      return (item as HTMLElement).offsetLeft;
    });
};

// const createOffsetArray = (el: HTMLDivElement) => {
//   return Array.from(el.querySelectorAll(".carousel-item")).map((item) => {
//     return (item as HTMLElement).offsetLeft;
//   });
// };

const BaseCarouselTheme = styled.div(
  () => `

  .carousel {
    overflow: hidden;
  }
  
  .carousel-inner {
    position: relative;
    display: flex;
    justify-content: flex-start;
    overflow: auto;
    height: 100%;
    &::-webkit-scrollbar {
      display: none;
    }
    > * {
      user-select: none;
      flex: 0 0 auto;
    }
  }
  

`
);

// const parent = element?.parentElement;
// if (sliderSnap && element && parent) {

// const resizeObserver = new ResizeObserver(handleResize);
// resizeObserver.observe(element.parentElement);

// handel resize event of carousel container
// const handleResize: ResizeObserverCallback = (
//     entries: ResizeObserverEntry[]
//   ) => {
//     entries.forEach((entry: ResizeObserverEntry) => {
//       const observedElementFirstChild = entry.target.firstElementChild;

//       if (observedElementFirstChild instanceof HTMLDivElement) {
//         createOffsetArray(observedElementFirstChild);
//       }
//     });
//   };

//   return Array.from(el.children)
//     .map((item) => (item as HTMLElement).offsetLeft)
//     .filter((offset, index) => index === 0 || offset !== 0);
